import { Observable, catchError, concatMap, of, timeout as rxTimeout, skip, startWith, take, } from 'rxjs';
import { calculateSettingsTimeout, sleep } from "../utils/index.js";
import { delayedMethod, delayedObservable, delayedSubject, getPulseDelayedInstance, } from "./instance.js";
function createSettingsService(settingDefaults = {}, settingAttributes = {}) {
    const timeoutMs = calculateSettingsTimeout();
    const getSettings = delayedMethod('get');
    const getSegments = delayedMethod('segments');
    const getExperiments = delayedMethod('experiments');
    const stateData = delayedMethod('stateData');
    const observeSetting = delayedObservable('observe');
    if (Object.keys(settingAttributes).length > 0) {
        stateData({
            ...Object.fromEntries(Object.entries(settingAttributes).map(([key, value]) => [`attr_${key}`, value])),
        });
    }
    return {
        get(name, tag, defaultValue) {
            const ctrl = new AbortController();
            return Promise.race([
                getSettings(name, tag, defaultValue ?? settingDefaults[name], settingAttributes).then((v) => {
                    ctrl.abort();
                    return v;
                }),
                sleep(timeoutMs, ctrl.signal).then(() => defaultValue),
            ]);
        },
        observe(name, tag, defaultValue) {
            const source$ = observeSetting(name, tag, defaultValue, settingAttributes);
            return new Observable((observer) => {
                const first$ = source$.pipe(take(1), rxTimeout(timeoutMs), catchError(() => of(defaultValue)));
                const rest$ = source$.pipe(skip(1));
                const merged$ = first$.pipe(concatMap((firstValue) => rest$.pipe(startWith(firstValue))));
                const sub = merged$.subscribe(observer);
                return () => sub.unsubscribe();
            });
        },
        segments() {
            const ctrl = new AbortController();
            return Promise.race([
                getSegments(settingAttributes).then((v) => {
                    ctrl.abort();
                    return v;
                }),
                sleep(timeoutMs, ctrl.signal).then(() => []),
            ]);
        },
        experiments() {
            const ctrl = new AbortController();
            return Promise.race([
                getExperiments(settingAttributes).then((v) => {
                    ctrl.abort();
                    return v;
                }),
                sleep(timeoutMs, ctrl.signal).then(() => []),
            ]);
        },
    };
}
function createTrackerService() {
    return {
        event: delayedMethod('event'),
        events: delayedMethod('events'),
        attribute: delayedMethod('attribute'),
        attributes: delayedMethod('attributes'),
        set: delayedMethod('set'),
        state: delayedMethod('state'),
        stateData: delayedMethod('stateData'),
        getState: delayedMethod('getState'),
        flush: delayedMethod('flush'),
        cmd: delayedMethod('cmd'),
        queueEmpty: delayedSubject('queueEmpty'),
    };
}
function createSharedService() {
    return {
        getStateString: delayedMethod('getStateString'),
    };
}
function createInternals(config) {
    return {
        pulseInit: [],
        ready: [],
        idu: globalThis.location?.href,
        c: config,
    };
}
function createPulseProxy(config) {
    async function pulseFn() {
        // eslint-disable-next-line prefer-rest-params
        const args = arguments;
        const instance = await getPulseDelayedInstance();
        if (typeof args[0] === 'function') {
            args[0](instance);
            return;
        }
        // eslint-disable-next-line prefer-spread
        instance.cmd.apply(instance, args);
    }
    // Reuse existing internals if global proxy already has them, otherwise create new
    const existingInternals = globalThis.pulse?.θ;
    const internals = existingInternals || createInternals(config);
    return Object.assign(pulseFn, createSettingsService(config?.defaults, config?.attributes), createTrackerService(), createSharedService(), { θ: internals });
}
export function createGlobalProxy(config) {
    if (typeof globalThis !== 'object') {
        throw new Error('Unsupported execution environment!');
    }
    if (!globalThis.pulse) {
        // Create minimal proxy structure first so getPulseDelayedInstance can access θ
        const internals = createInternals(config);
        const minimalProxy = { θ: internals };
        globalThis.pulse = minimalProxy;
        // Now create the full proxy safely
        const proxy = createPulseProxy(config);
        globalThis.pulse = proxy;
    }
    else {
        if (globalThis.pulse.θ?.c?.app &&
            config?.app &&
            globalThis.pulse.θ?.c?.app !== config.app) {
            throw new Error('Pulse is already initialized with a different app id!');
        }
    }
    return globalThis.pulse;
}
export function createScopedProxy(config) {
    return createPulseProxy(config);
}
//# sourceMappingURL=proxy.js.map